import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, Envelope, Phone, MapPin, ArrowRight, Buildings } from '@phosphor-icons/react';
import MarketingNav from '../../components/marketing/MarketingNav';
import MarketingFooter from '../../components/marketing/MarketingFooter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Contact-Sales page. Left column: value prop + direct contact info.
 * Right column: sales-lead form. Submissions hit /api/marketing/contact
 * which persists to Mongo (super-admin can read via /api/marketing/leads)
 * and fires a SendGrid email if configured.
 */
export default function Contact() {
  const [form, setForm] = useState({
    name: '', company: '', email: '', phone: '',
    employees: '', interested_plan: '', message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Contact Sales — Vooxy';
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/marketing/contact`, form);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050B18] text-white antialiased" data-testid="contact-success">
        <MarketingNav />
        <section className="pt-40 pb-32 relative overflow-hidden">
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-emerald-500/[0.08] blur-[120px]" />
          </div>
          <div className="max-w-2xl mx-auto px-6 lg:px-10 text-center">
            <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/30 grid place-items-center mx-auto mb-8">
              <CheckCircle size={40} weight="fill" className="text-emerald-400" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Thanks, {form.name.split(' ')[0]}!
            </h1>
            <p className="text-lg text-slate-400 leading-relaxed mb-10">
              We&apos;ve received your request and our team will reach out to <span className="text-emerald-300">{form.email}</span> within one business day. In the meantime, feel free to explore our pricing.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="/pricing"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-emerald-500 text-[#050B18] font-semibold hover:bg-emerald-400 transition-colors"
              >
                See pricing plans
                <ArrowRight size={16} weight="bold" />
              </a>
              <a
                href="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-white/10 text-slate-200 hover:border-white/20 transition-colors"
              >
                Back to home
              </a>
            </div>
          </div>
        </section>
        <MarketingFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050B18] text-white antialiased" data-testid="marketing-contact">
      <MarketingNav />

      <section className="pt-32 lg:pt-40 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-emerald-500/[0.06] blur-[120px]" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Left — value prop */}
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-4">Talk to sales</div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Let&apos;s build your workspace.
              </h1>
              <p className="text-lg text-slate-400 leading-relaxed mb-10">
                Tell us about your team&apos;s approval hierarchy, product catalog, and existing tools. We&apos;ll come back within a business day with a tailored walkthrough — and a plan recommendation that fits.
              </p>

              <ul className="space-y-5 mb-10">
                {[
                  ['30-min tailored demo', 'We map YOUR approval chain onto Vooxy — live, not a slide deck.'],
                  ['Migration assessment', 'If you\'re coming from Zoho, HubSpot, or spreadsheets, we\'ll scope the switch.'],
                  ['Custom pricing for 50+ seats', 'Enterprise plans can be flexed — annual, multi-year, or seat-based.'],
                ].map(([t, d]) => (
                  <li key={t} className="flex gap-4">
                    <CheckCircle size={22} weight="fill" className="text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-semibold text-white mb-1">{t}</div>
                      <div className="text-sm text-slate-400">{d}</div>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="pt-10 border-t border-white/5 space-y-4">
                <div className="flex items-center gap-3 text-sm text-slate-400">
                  <Envelope size={18} className="text-emerald-400 flex-shrink-0" />
                  <a href="mailto:sales@vooxy.app" className="hover:text-white transition-colors">sales@vooxy.app</a>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-400">
                  <MapPin size={18} className="text-emerald-400 flex-shrink-0" />
                  <span>United States · serving global customers</span>
                </div>
              </div>
            </div>

            {/* Right — form */}
            <div className="lg:mt-8">
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="rounded-2xl bg-white/[0.03] border border-white/10 p-6 sm:p-8 backdrop-blur-sm"
              >
                {error && (
                  <div className="mb-5 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm" data-testid="contact-error">
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <Field label="Your name" required>
                    <input
                      value={form.name} onChange={update('name')}
                      data-testid="contact-name"
                      required autoComplete="name"
                      placeholder="Priya Sharma"
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Work email" required>
                    <input
                      type="email" value={form.email} onChange={update('email')}
                      data-testid="contact-email"
                      required autoComplete="email"
                      placeholder="priya@acme.com"
                      className={inputClass}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <Field label="Company" required>
                    <input
                      value={form.company} onChange={update('company')}
                      data-testid="contact-company"
                      required autoComplete="organization"
                      placeholder="Acme Manufacturing Pvt Ltd"
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Phone (optional)">
                    <input
                      value={form.phone} onChange={update('phone')}
                      data-testid="contact-phone"
                      autoComplete="tel"
                      placeholder="+91 98XXX XXXXX"
                      className={inputClass}
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <Field label="Team size">
                    <select
                      value={form.employees} onChange={update('employees')}
                      data-testid="contact-employees"
                      className={inputClass}
                    >
                      <option value="">Select…</option>
                      <option value="1-10">1–10</option>
                      <option value="11-50">11–50</option>
                      <option value="51-200">51–200</option>
                      <option value="201-500">201–500</option>
                      <option value="500+">500+</option>
                    </select>
                  </Field>
                  <Field label="Plan of interest">
                    <select
                      value={form.interested_plan} onChange={update('interested_plan')}
                      data-testid="contact-plan"
                      className={inputClass}
                    >
                      <option value="">Not sure yet</option>
                      <option value="Standard">Standard</option>
                      <option value="Professional">Professional</option>
                      <option value="Enterprise">Enterprise</option>
                      <option value="Ultimate">Ultimate</option>
                    </select>
                  </Field>
                </div>

                <Field label="Anything else we should know? (optional)">
                  <textarea
                    value={form.message} onChange={update('message')}
                    data-testid="contact-message"
                    rows={4}
                    placeholder="Current tools, approval hierarchy, must-have features…"
                    className={`${inputClass} resize-none`}
                    maxLength={2000}
                  />
                </Field>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-emerald-500 text-[#050B18] font-bold hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-emerald-500/25"
                >
                  {submitting ? (
                    <span>Sending…</span>
                  ) : (
                    <>
                      Send message
                      <ArrowRight size={18} weight="bold" />
                    </>
                  )}
                </button>

                <p className="mt-4 text-xs text-slate-500 text-center">
                  By submitting, you agree to be contacted by Vooxy. We&apos;ll never share your details.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

const inputClass =
  'w-full px-4 py-2.5 rounded-lg bg-[#050B18] border border-white/10 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30 transition-all';

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1.5">
        {label} {required && <span className="text-emerald-400">*</span>}
      </div>
      {children}
    </label>
  );
}
